
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Clock, CheckCircle, Star } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  duration_minutes: number;
  price: number;
  currency: string;
  description?: string;
  is_free_session?: boolean;
  formats_available?: string[];
  therapy_tags?: ServiceTherapyTag[];
}

interface PractitionerServicesProps {
  services: Service[];
  selectedService: string | null;
  onServiceSelect: (serviceId: string | null) => void;
  onBookService: (serviceId: string) => void;
  practitionerId?: string;
  practitionerName?: string;
  professionalTitle?: string;
  avatarUrl?: string;
  practitionerSlug?: string;
  practitionerTimezone?: string;
}

const PractitionerServices: React.FC<PractitionerServicesProps> = ({
  services,
  selectedService,
  onServiceSelect,
  onBookService,
  practitionerId,
  practitionerName,
  professionalTitle,
  avatarUrl,
  practitionerSlug,
  practitionerTimezone
}) => {
  const navigate = useNavigate();
  const { user, activeRole } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('practitioners');
  const [showRoleAlert, setShowRoleAlert] = React.useState(false);

  const handleBookNow = (service: Service) => {
    console.log("Current active role:", activeRole);

    // Check if user is not logged in
    if (!user) {
      toast({
        title: "Login Required",
        description: t('loginRequired'),
        variant: "default"
      });
      navigate('/client-auth');
      return;
    }

    // Check if user is practitioner or admin
    if (activeRole === 'practitioner' || activeRole === 'admin') {
      setShowRoleAlert(true);
      return;
    }

    // Check if client role
    if (activeRole === 'client') {
      // Validate required data
      if (!practitionerId || !practitionerName) {
        toast({
          variant: "destructive",
          title: "Missing practitioner information",
          description: t('selectPractitionerAgain')
        });
        return;
      }

      // Store temporary booking information for the date/time selection page
      const returnToPath = `/book-an-appointment/${practitionerSlug || practitionerId}`;
      const tempBookingInfo = {
        practitionerId: practitionerId,
        practitionerName: practitionerName || 'Unknown Practitioner',
        serviceId: service.id,
        serviceName: service.service_name,
        price: service.price,
        currency: service.currency,
        duration: service.duration_minutes,
        professionalTitle: professionalTitle,
        avatarUrl: avatarUrl,
        returnToPath,
        practitionerTimezone: practitionerTimezone || 'America/New_York' // Include practitioner timezone
      };
      console.log("Storing temporary booking info with timezone:", tempBookingInfo);
      sessionStorage.setItem('tempBookingInfo', JSON.stringify(tempBookingInfo));

      // Also clear any existing bookingInfo to prevent conflicts
      sessionStorage.removeItem('bookingInfo');

      // Navigate to date/time selection page
      navigate('/booking-datetime');
    }
  };

  const handleGoToClientLogin = () => {
    navigate('/client-auth');
    setShowRoleAlert(false);
  };

  if (!services || services.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <p className="text-gray-600">{t('noServicesAvailable')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <div className="space-y-3">
          {services.map((service) => (
            <Card 
              key={service.id} 
              className={`cursor-pointer transition-colors ${
                selectedService === service.id 
                  ? 'border-amber-500 bg-amber-50' 
                  : 'hover:border-amber-200'
              }`}
              onClick={() => onServiceSelect(service.id)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-lg">{service.service_name}</h4>
                      {selectedService === service.id && (
                        <CheckCircle className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{service.duration_minutes} {t('minutes')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="font-medium text-lg text-gray-900">
                          {typeof service.price === 'number' ? service.price.toFixed(2) : '-'}
                        </span>
                        <span className="text-xs text-gray-500">{service.currency || 'USD'}</span>
                      </div>
                    </div>

                    {service.description && (
                      <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                    )}
                    
                    {service.therapy_tags && service.therapy_tags.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex flex-wrap gap-1.5">
                          {service.therapy_tags
                            .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                            .map((tag) => (
                              <Badge 
                                key={tag.id} 
                                variant={tag.is_primary ? "default" : "secondary"}
                                className={`text-xs ${tag.is_primary ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
                              >
                                {tag.is_primary && <Star className="h-3 w-3 mr-1 fill-current" />}
                                {i18n.language === 'zh' ? tag.therapy_modality.name_cn : tag.therapy_modality.name_en}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {service.formats_available?.includes('virtual') && (
                  <Badge variant="outline">{t('virtual')}</Badge>
                )}
                {service.formats_available?.includes('in_person') && (
                  <Badge variant="outline">{t('inPerson')}</Badge>
                )}
              </div>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookNow(service);
                    }}
                    size="sm"
                    className="ml-3"
                  >
                    {t('bookNow')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Role Access Alert Dialog */}
      <AlertDialog open={showRoleAlert} onOpenChange={setShowRoleAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountTypeRestriction')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('practitionerAdminCantBook')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowRoleAlert(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGoToClientLogin}>{t('goToClientLogin')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PractitionerServices;
